import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/contexts/AuthContext";
import axios from "axios";
import NudgeCard from "@/components/NudgeCard";
import WhatsAppButton from "@/components/WhatsAppButton";
import ChatSuporte from "@/components/ChatSuporte";
import FeatureSuggestionBanner from "@/components/FeatureSuggestionBanner";
import {
  Scale, LayoutDashboard, FileText, Bell, Calendar, Users, Wallet,
  Settings, Crown, LogOut, Menu, X, ChevronRight, Mail, SquareKanban, FileSignature, Bot, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/processos", icon: FileText, label: "Processos" },
  { path: "/clientes", icon: Users, label: "Clientes" },
  { path: "/jurisprudencias", icon: Scale, label: "Jurisprudências" },
  { path: "/intimacoes", icon: Bell, label: "Intimações" },
  { path: "/agenda", icon: Calendar, label: "Agenda" },
  { path: "/quadros", icon: SquareKanban, label: "Quadros" },
  { path: "/documentos", icon: FileSignature, label: "Documentos" },
  { path: "/assistente", icon: Bot, label: "Assistente" },
  { path: "/configuracoes", icon: Settings, label: "Configurações" },
  { path: "/plano", icon: Crown, label: "Plano" },
];

const hasFinanceCapability = (user) => user?.capabilities?.finance === true || user?.entitlements?.finance === true || user?.financial_access?.allowed === true;
// Sem a fase ligada a API de clientes responde 503; esconder o item evita
// levar o usuário a uma tela que só sabe quebrar.
const hasClientesCapability = (user) => user?.capabilities?.clientes !== false;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [counts, setCounts] = useState({ processos: null, intimacoes: null, preCadastro: 0 });
  const withFinance = hasFinanceCapability(user)
    ? [...navItems.slice(0, 3), { path: "/financeiro", icon: Wallet, label: "Finanças" }, ...navItems.slice(3)]
    : navItems;
  const comClientes = hasClientesCapability(user) ? withFinance : withFinance.filter(item => item.path !== "/clientes");
  // O pré-cadastro fica sempre visível, logo abaixo de Processos.
  //
  // Escondê-lo quando vazio parecia limpeza, mas impedia o advogado de
  // entender a separação que o sistema faz: "Processos" é a carteira dele,
  // "Pré-cadastro" é o que a busca por OAB achou e ele ainda não decidiu.
  // Sem ver as duas, as duas viram a mesma coisa na cabeça dele.
  const visibleNavItems = comClientes.flatMap((item) => item.path === "/processos"
    ? [item, { path: "/pre-cadastro", icon: Inbox, label: "Fila de processos" }]
    : [item]);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/payments/counts`, { withCredentials: true })
      .then((res) => { if (active) setCounts({
        processos: res.data?.processos_count,
        intimacoes: res.data?.intimacoes_pendentes,
        preCadastro: res.data?.pre_cadastro_pendentes || 0,
      }); })
      .catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  const badgeFor = (path) => {
    if (path === "/processos") return counts.processos;
    if (path === "/intimacoes") return counts.intimacoes;
    if (path === "/pre-cadastro") return counts.preCadastro;
    return null;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB]">
      {/* Faixas no fluxo normal (empurram o conteúdo, não cobrem) */}
      <FeatureSuggestionBanner />
      {user?.impersonated_by && (
        <div className="w-full z-[100] bg-amber-500 text-[#001533] text-sm font-medium px-4 py-2 flex items-center justify-center gap-3">
          👁️ Você está vendo como <strong>{user?.name}</strong> (impersonação por {user.impersonated_by})
          <button onClick={() => logout().then(() => { window.location.href = "/painel-x7k2m9q4f3"; })}
            className="underline font-semibold">Encerrar</button>
        </div>
      )}
      {user?.needs_upgrade && user?.plan !== "pro" && (
        <div className="w-full z-[90] bg-[#003882] text-white text-sm px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap">
          <Crown size={16} className="text-amber-300" />
          <span><strong>Limite do plano FREE atingido (100 processos).</strong> Novos processos não são mais adicionados — seguimos monitorando os atuais. Faça upgrade para o PRO e monitore <strong>todos sem limite</strong>.</span>
          <Link to="/plano" className="bg-white text-[#003882] font-semibold rounded-md px-3 py-1 text-xs hover:bg-blue-50">
            Fazer upgrade
          </Link>
        </div>
      )}
      {!user?.needs_upgrade && user?.plan !== "pro" && counts.processos >= 75 && (
        <div className="w-full z-[89] bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap">
          <Crown size={15} className="text-amber-500 flex-shrink-0" />
          <span>Você usou <strong>{counts.processos} de 100 processos</strong> do plano FREE. Faça upgrade antes de atingir o limite.</span>
          <Link to="/plano" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-md px-3 py-1 text-xs">
            Ver PRO
          </Link>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-[#D1DEEE] sticky top-0 h-screen">
        {/* Logo */}
        <div className="flex items-center px-6 py-5 border-b border-[#D1DEEE]">
          <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="w-full h-auto" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`sidebar-link ${isActive ? "active" : ""}`}
              >
                <item.icon size={20} strokeWidth={1.5} />
                <span>{item.label}</span>
                {badgeFor(item.path) != null && badgeFor(item.path) > 0 && (
                  <span className="ml-auto bg-[#003882] text-white text-xs font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                    {badgeFor(item.path)}
                  </span>
                )}
                {isActive && badgeFor(item.path) == null && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Footer — clicar no nome abre a opção Sair */}
        <div className="p-4 border-t border-[#D1DEEE]">
          {userMenuOpen && (
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors text-sm font-medium"
            >
              <LogOut size={16} />
              Sair
            </button>
          )}
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            data-testid="user-menu-toggle"
            className="w-full flex items-center gap-3 rounded-lg p-1 hover:bg-[#F4F7FB] transition-colors text-left"
          >
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#e5edf5] text-[#003882] text-sm font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-[#001533] text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-[#335480] text-xs truncate">{user?.email}</p>
            </div>
          </button>
        </div>

        {/* Versão */}
        <div className="p-3 border-t border-[#D1DEEE]">
          <p className="text-center text-[#D1DEEE] text-xs">
            v{process.env.REACT_APP_VERSION || "0.1.0"}
          </p>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#D1DEEE] px-4 h-14 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="h-8 w-auto" />
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="text-[#335480] p-1"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="absolute left-0 top-14 bottom-0 w-64 bg-white border-r border-[#D1DEEE] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="flex-1 px-3 py-4 space-y-1">
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActive ? "active" : ""}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <item.icon size={20} strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-[#D1DEEE]">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors text-sm"
              >
                <LogOut size={16} />
                Sair
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:min-h-screen pt-14 lg:pt-0 overflow-auto flex flex-col">
        <div className="flex-1">{children}</div>

        {/* Footer área logada */}
        <footer className="border-t border-[#D1DEEE] bg-white px-6 py-6 mt-8">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
              <a
                href="https://www.kosmosai.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#335480] hover:text-[#003882] transition-colors"
              >
                Powered by <span className="font-bold">Kosmos</span>
              </a>
              <Link to="/termos-de-uso" className="text-[#335480] hover:text-[#003882] transition-colors">
                Termos de Uso
              </Link>
              <Link to="/politica-de-privacidade" className="text-[#335480] hover:text-[#003882] transition-colors">
                Política de Privacidade
              </Link>
              <a
                href="mailto:contato@kosmosai.com.br"
                className="flex items-center gap-1.5 text-[#335480] hover:text-[#003882] transition-colors"
              >
                <Mail size={14} /> Fale conosco
              </a>
            </div>
            <p className="text-[#9bb0cc] text-xs text-center md:text-right">
              CNPJ 08.277.372/0001-71 — Keep Soluções Tecnológicas Ltda. · v{process.env.REACT_APP_VERSION || "0.1.0"}
            </p>
          </div>
        </footer>
      </main>
      </div>
      {!user?.impersonated_by && <NudgeCard />}
      <WhatsAppButton href="https://wa.me/message/7G7QKQ4PM56KM1" />
      <ChatSuporte api={API} />
    </div>
  );
}
